import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchPatientInsurances, updatePatientInsurance, deletePatientInsurance,
  promotePatientInsurancePrimary, fetchPayers, changeClaimPayer, voidClaimAtPayer,
} from '../services/api';
import './ChangePayerModal.css';

const ORDER_LABELS = { 1: 'Primary', 2: 'Secondary', 3: 'Tertiary' };

/**
 * Reassign the payer billed on a claim at one COB level.
 *
 * Props:
 *   claimAtLevel  { id, claim_number, claim_status, status (payment_status) } for THIS level
 *   order         1 | 2 | 3
 *   patientId     number
 *   currentPayer  { payer_name | insurance_provider, insured_id | policy_number } (display only)
 *   onClose()     close without changes
 *   onChanged()   refresh callback after a successful change
 */
export default function ChangePayerModal({ claimAtLevel, order, patientId, currentPayer, onClose, onChanged }) {
  const navigate = useNavigate();

  const [loading, setLoading]   = useState(true);
  const [insurances, setInsurances] = useState([]); // active slots 1-3
  const [payers, setPayers]     = useState([]);

  const [mode, setMode]         = useState('existing'); // 'existing' | 'new'
  const [selectedSlot, setSelectedSlot] = useState(null);

  // New-payer form
  const [payerId, setPayerId]         = useState(null);
  const [payerName, setPayerName]     = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [payerSearch, setPayerSearch] = useState('');
  const [payerDropdownOpen, setPayerDropdownOpen] = useState(false);
  const [fullSlotChoice, setFullSlotChoice] = useState('claim_only'); // 'replace' | 'claim_only'
  const [replaceSlot, setReplaceSlot] = useState(null);

  const [rebillReason, setRebillReason] = useState('');
  const [withdrawOriginal, setWithdrawOriginal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);
  const [refundConfirm, setRefundConfirm] = useState(null); // { paid_amount }
  const [dupConfirm, setDupConfirm] = useState(null);       // { name, order }
  const [result, setResult]         = useState(null);       // { mode, newClaimId, newClaimNumber, message }

  useEffect(() => {
    let active = true;
    Promise.all([fetchPatientInsurances(patientId), fetchPayers()])
      .then(([insRes, payRes]) => {
        if (!active) return;
        const slots = (insRes.data || []).filter((r) => r.coverage_order >= 1 && r.coverage_order <= 3);
        setInsurances(slots);
        setPayers(payRes.data || []);
      })
      .catch(() => active && setError('Failed to load insurance and payer data.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [patientId]);

  // Pre-submission preview — server is authoritative, this just drives messaging.
  const preSubmission = useMemo(() => {
    const cs = claimAtLevel?.claim_status;
    const ps = claimAtLevel?.status ?? claimAtLevel?.payment_status;
    return ['ready', 'generated'].includes(cs) && ps === 'pending';
  }, [claimAtLevel]);

  const occupied = useMemo(() => new Set(insurances.map((i) => i.coverage_order)), [insurances]);
  const freeSlot = useMemo(() => [1, 2, 3].find((s) => !occupied.has(s)) ?? null, [occupied]);
  const allFull  = freeSlot === null;

  const filteredPayers = useMemo(() => {
    const q = payerSearch.trim().toLowerCase();
    return payers
      .filter((p) => !p.is_self_pay_payer && (!q || p.name.toLowerCase().includes(q)))
      .slice(0, 8);
  }, [payers, payerSearch]);

  // Pre-submission must land in an active slot, so "claim only" isn't offered then.
  const effectiveFullChoice = preSubmission ? 'replace' : fullSlotChoice;

  const canSubmit = (() => {
    if (mode === 'existing') return selectedSlot != null;
    if (!payerName.trim() || !policyNumber.trim()) return false;
    if (order > 1 && allFull && effectiveFullChoice === 'replace' && replaceSlot == null) return false;
    return true;
  })();

  const selectPayer = (p) => {
    setPayerId(p.payer_id);
    setPayerName(p.name);
    setPayerSearch('');
    setPayerDropdownOpen(false);
  };

  const buildNewPayer = async () => {
    if (mode === 'existing') {
      return { from_patient_slot: selectedSlot };
    }
    const insPayload = {
      insurance_payer_id: payerId,
      insurance_provider: payerName.trim(),
      policy_number:      policyNumber.trim(),
      effective_date:     effectiveDate || null,
    };

    // Primary card correction: the new payer becomes the patient's PRIMARY (slot 1).
    // The old primary slides to the next open slot, or is archived if 2 & 3 are full.
    // Done atomically server-side (single transaction) so a mid-flow failure can't
    // leave slot 1 empty with the old primary stranded in 2/3 or archived.
    if (order === 1) {
      await promotePatientInsurancePrimary(patientId, insPayload);
      return { from_patient_slot: 1 };
    }

    if (!allFull) {
      await updatePatientInsurance(patientId, freeSlot, insPayload);
      return { from_patient_slot: freeSlot };
    }
    if (effectiveFullChoice === 'replace') {
      await deletePatientInsurance(patientId, replaceSlot); // archives the old slot
      await updatePatientInsurance(patientId, replaceSlot, insPayload);
      return { from_patient_slot: replaceSlot };
    }
    // claim-only snapshot (does not touch the patient's active coverage)
    return { snapshot: { insurance_payer_id: payerId, payer_name: payerName.trim(), insured_id: policyNumber.trim() } };
  };

  const submit = async ({ confirmRefund = false, confirmDuplicate = false } = {}) => {
    setSubmitting(true);
    setError(null);
    try {
      const new_payer = await buildNewPayer();
      const res = await changeClaimPayer(claimAtLevel.id, {
        payer_order: order,
        new_payer,
        rebill_reason: rebillReason.trim() || null,
        confirm_refund_recoup: confirmRefund,
        confirm_duplicate_payer: confirmDuplicate,
      });
      // Optionally withdraw the original from its payer (837 freq-8 void) after a rebill.
      // `res` is already the response body — the axios interceptor unwraps `response.data`.
      let finalResult = res;
      if (res.mode === 'rebill' && withdrawOriginal) {
        try {
          await voidClaimAtPayer({ claim_id: claimAtLevel.id });
          finalResult = { ...res, voidNote: 'The original claim was withdrawn from its payer (void transmitted to the clearinghouse).' };
        } catch (e) {
          const vd = e?.response?.data;
          finalResult = {
            ...res,
            voidNote: vd?.requires_control_number
              ? 'Rebill complete. Could not auto-withdraw the original — no payer claim control number on file yet; void it manually once the ERA posts.'
              : `Rebill complete, but withdrawing the original failed: ${vd?.error || 'unknown error'}`,
          };
        }
      }
      setRefundConfirm(null);
      setDupConfirm(null);
      setResult(finalResult);
      onChanged?.();
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.requires_confirmation === 'refund_recoup') {
        setRefundConfirm({ paid_amount: data.paid_amount });
      } else if (err?.response?.status === 409 && data?.requires_confirmation === 'duplicate_payer') {
        setDupConfirm({ name: data.duplicate_payer_name, order: data.duplicate_at_order });
      } else {
        setError(data?.error || 'Failed to change the payer.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const label = ORDER_LABELS[order];

  return (
    <div className="cpm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="cpm-modal">
        <div className="cpm-header">
          <div>
            <h2 className="cpm-title">Change {label} Payer</h2>
            <p className="cpm-subtitle">
              Claim {claimAtLevel?.claim_number} · current: {currentPayer?.payer_name || currentPayer?.insurance_provider || '—'}
            </p>
          </div>
          <button className="cpm-close" onClick={onClose} disabled={submitting}>×</button>
        </div>

        {loading ? (
          <div className="cpm-body"><p className="cpm-muted">Loading…</p></div>
        ) : result ? (
          <div className="cpm-body">
            <div className="cpm-success">
              <p>{result.message}</p>
              {result.mode === 'rebill' && (
                <p className="cpm-muted">
                  The original claim stays open to receive its payer&apos;s response. The rebill is in
                  the worklist as <strong>{result.newClaimNumber}</strong>, ready to review and submit.
                </p>
              )}
              {result.voidNote && <p className="cpm-muted">{result.voidNote}</p>}
            </div>
            <div className="cpm-actions">
              {result.mode === 'rebill' && result.newClaimId && (
                <button className="cpm-btn-primary" onClick={() => { navigate(`/claims/${result.newClaimId}`); onClose(); }}>
                  Go to rebilled claim
                </button>
              )}
              <button className="cpm-btn-secondary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : refundConfirm ? (
          <div className="cpm-body">
            <div className="cpm-warning">
              <strong>This payer has already paid ${Number(refundConfirm.paid_amount).toFixed(2)}.</strong>
              <p>Switching payers means this ${Number(refundConfirm.paid_amount).toFixed(2)} is owed back to the
                 original payer. It will be logged as a <strong>credit balance</strong> (due in 60 days) for you to
                 refund or recoup from the Credit Balances worklist. The original claim and its remit are preserved. Continue?</p>
            </div>
            {error && <p className="cpm-error">{error}</p>}
            <div className="cpm-actions">
              <button className="cpm-btn-secondary" onClick={() => setRefundConfirm(null)} disabled={submitting}>Cancel</button>
              <button className="cpm-btn-danger" onClick={() => submit({ confirmRefund: true })} disabled={submitting}>
                {submitting ? 'Working…' : 'Switch payer anyway'}
              </button>
            </div>
          </div>
        ) : dupConfirm ? (
          <div className="cpm-body">
            <div className="cpm-warning">
              <strong>{dupConfirm.name} is already on this claim as {ORDER_LABELS[dupConfirm.order] || `payer ${dupConfirm.order}`}.</strong>
              <p>Assigning {dupConfirm.name} here leaves the same payer in two slots. Only the primary
                 (payer&nbsp;1) is transmitted to the clearinghouse, so this won&apos;t double-bill — but you may
                 have meant to update a different slot or swap the order. Continue, or cancel and pick a
                 different payer.</p>
            </div>
            {error && <p className="cpm-error">{error}</p>}
            <div className="cpm-actions">
              <button className="cpm-btn-secondary" onClick={() => setDupConfirm(null)} disabled={submitting}>Cancel</button>
              <button className="cpm-btn-danger" onClick={() => submit({ confirmRefund: true, confirmDuplicate: true })} disabled={submitting}>
                {submitting ? 'Working…' : 'Use this payer anyway'}
              </button>
            </div>
          </div>
        ) : (
          <div className="cpm-body">
            <div className={`cpm-mech cpm-mech--${preSubmission ? 'inplace' : 'rebill'}`}>
              {preSubmission
                ? 'This claim has not been submitted — the payer will be updated in place.'
                : 'This claim has been submitted — it will be rebilled as a new claim under the new payer. The original stays open to receive its denial.'}
            </div>

            <div className="cpm-tabs">
              <button className={`cpm-tab ${mode === 'existing' ? 'active' : ''}`} onClick={() => setMode('existing')}>
                Existing coverage
              </button>
              <button className={`cpm-tab ${mode === 'new' ? 'active' : ''}`} onClick={() => setMode('new')}>
                Add new payer
              </button>
            </div>

            {mode === 'existing' ? (
              <div className="cpm-slots">
                {insurances.length === 0 ? (
                  <p className="cpm-muted">No insurance slots on file. Use “Add new payer”.</p>
                ) : insurances.map((ins) => (
                  <label key={ins.coverage_order} className={`cpm-slot ${selectedSlot === ins.coverage_order ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="cpm-slot"
                      checked={selectedSlot === ins.coverage_order}
                      onChange={() => setSelectedSlot(ins.coverage_order)}
                    />
                    <span className="cpm-slot-badge">{ORDER_LABELS[ins.coverage_order]}</span>
                    <span className="cpm-slot-name">{ins.insurance_provider}</span>
                    {ins.policy_number && <span className="cpm-slot-meta">ID: {ins.policy_number}</span>}
                  </label>
                ))}
              </div>
            ) : (
              <div className="cpm-newpayer">
                <label className="cpm-field-label">Payer</label>
                {payerId ? (
                  <div className="cpm-payer-selected">
                    <span>{payerName}</span>
                    <button onClick={() => { setPayerId(null); setPayerName(''); }}>Change</button>
                  </div>
                ) : (
                  <div className="cpm-combobox">
                    <input
                      className="cpm-input"
                      placeholder="Search payers…"
                      value={payerSearch}
                      onChange={(e) => { setPayerSearch(e.target.value); setPayerDropdownOpen(true); }}
                      onFocus={() => setPayerDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setPayerDropdownOpen(false), 150)}
                    />
                    {payerDropdownOpen && filteredPayers.length > 0 && (
                      <div className="cpm-dropdown">
                        {filteredPayers.map((p) => (
                          <button key={p.payer_id} className="cpm-dropdown-item" onMouseDown={() => selectPayer(p)}>
                            {p.name}{p.payer_code && <span className="cpm-dropdown-code">{p.payer_code}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="cpm-field-row">
                  <div className="cpm-field">
                    <label className="cpm-field-label">Member / Policy ID</label>
                    <input className="cpm-input" value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} placeholder="Policy #" />
                  </div>
                  <div className="cpm-field">
                    <label className="cpm-field-label">Effective Date</label>
                    <input className="cpm-input" type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
                  </div>
                </div>

                {order === 1 ? (
                  <p className="cpm-hint">
                    Becomes the patient&apos;s primary insurance. The current primary moves to the next open slot,
                    or is archived if all slots are full.
                  </p>
                ) : !allFull ? (
                  <p className="cpm-hint">Will be added as the patient&apos;s {ORDER_LABELS[freeSlot]} insurance.</p>
                ) : (
                  <div className="cpm-full">
                    <p className="cpm-hint">All three insurance slots are full. Choose how to file the new payer:</p>
                    {!preSubmission && (
                      <label className="cpm-radio">
                        <input type="radio" name="cpm-full" checked={effectiveFullChoice === 'claim_only'} onChange={() => setFullSlotChoice('claim_only')} />
                        Use for this claim only (don&apos;t change the patient&apos;s active coverage)
                      </label>
                    )}
                    <label className="cpm-radio">
                      <input type="radio" name="cpm-full" checked={effectiveFullChoice === 'replace'} onChange={() => setFullSlotChoice('replace')} />
                      Archive an existing slot and use it
                    </label>
                    {effectiveFullChoice === 'replace' && (
                      <div className="cpm-replace-slots">
                        {insurances.map((ins) => (
                          <label key={ins.coverage_order} className={`cpm-slot ${replaceSlot === ins.coverage_order ? 'selected' : ''}`}>
                            <input type="radio" name="cpm-replace" checked={replaceSlot === ins.coverage_order} onChange={() => setReplaceSlot(ins.coverage_order)} />
                            <span className="cpm-slot-badge">{ORDER_LABELS[ins.coverage_order]}</span>
                            <span className="cpm-slot-name">{ins.insurance_provider}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!preSubmission && (
              <div className="cpm-field">
                <label className="cpm-field-label">Reason (optional)</label>
                <input className="cpm-input" value={rebillReason} onChange={(e) => setRebillReason(e.target.value)} placeholder="e.g. wrong payer billed" />
              </div>
            )}

            {!preSubmission && (
              <label className="cpm-radio cpm-withdraw">
                <input type="checkbox" checked={withdrawOriginal} onChange={(e) => setWithdrawOriginal(e.target.checked)} />
                Also withdraw the original from {currentPayer?.payer_name || currentPayer?.insurance_provider || 'its payer'} (transmits an 837 void). Avoids a double payment if that payer hasn&apos;t responded yet.
              </label>
            )}

            {error && <p className="cpm-error">{error}</p>}

            <div className="cpm-actions">
              <button className="cpm-btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
              <button className="cpm-btn-primary" onClick={() => submit()} disabled={!canSubmit || submitting}>
                {submitting ? 'Working…' : preSubmission ? 'Update payer' : 'Rebill claim'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
